"use client";

import React from "react";
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from "recharts";
import {
	Card,
	Button,
	Select,
	SelectItem,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownTrigger,
	cn,
} from "@heroui/react";
import { Icon } from "@iconify/react";

const data = [
	{
		title: "Traffic Sources",
		categories: ["Search", "Direct", "Social", "Referral"],
		color: "warning",
		chartData: [
			{ name: "Search", value: 400 },
			{ name: "Direct", value: 300 },
			{ name: "Social", value: 300 },
			{ name: "Referral", value: 200 },
		],
	},
	{
		title: "Device Usage",
		categories: ["Mobile", "Desktop", "Tablet", "Smart TV"],
		color: "success",
		chartData: [
			{ name: "Mobile", value: 450 },
			{ name: "Desktop", value: 300 },
			{ name: "Tablet", value: 250 },
			{ name: "Smart TV", value: 200 },
		],
	},
	{
		title: "Browser Usage",
		categories: ["Chrome", "Safari", "Firefox", "Edge"],
		color: "danger",
		chartData: [
			{ name: "Chrome", value: 350 },
			{ name: "Safari", value: 280 },
			{ name: "Firefox", value: 220 },
			{ name: "Edge", value: 150 },
		],
	},
];

export default function DemoPage() {
	return (
		<dl className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
			{data.map((item, index) => (
				<CircleChartCard key={index} {...item} />
			))}
		</dl>
	);
}

const formatTotal = (total) => {
	return total >= 1000 ? `${(total / 1000).toFixed(1)}K` : total;
};

const CircleChartCard = React.forwardRef(
	({ className, title, categories, color, chartData, ...props }, ref) => {
		return (
			<Card
				ref={ref}
				className={cn(
					"dark:border-default-100 min-h-[240px] border border-transparent",
					className,
				)}
				{...props}
			>
				<div className="flex h-full flex-wrap items-center justify-center gap-x-2 lg:flex-nowrap">
					<ResponsiveContainer
						className="[&_.recharts-surface]:outline-hidden"
						height={200}
						width="100%"
					>
						<PieChart
							accessibilityLayer
							margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
						>
							<Tooltip
								content={({ label, payload }) => (
									<div className="rounded-medium bg-background text-tiny shadow-small flex h-8 min-w-[120px] items-center gap-x-2 px-1">
										<span className="text-foreground font-medium">
											{label}
										</span>
										{payload?.map((p) => {
											const name = p.name;
											const value = p.value;
											const category =
												categories.find(
													(c) =>
														c.toLowerCase() ===
														name,
												) ?? name;
											const index = chartData.findIndex(
												(c) => c.name === name,
											);

											return (
												<div
													key={`${index}-${name}`}
													className="flex w-full items-center gap-x-2"
												>
													<div
														className="h-2 w-2 flex-none rounded-full"
														style={{
															backgroundColor: `hsl(var(--heroui-${color}-${(index + 1) * 200}))`,
														}}
													/>
													<div className="text-default-700 flex w-full items-center justify-between gap-x-2 pr-1 text-xs">
														<span className="text-default-500">
															{category}
														</span>
														<span className="text-default-700 font-mono font-medium">
															{formatTotal(value)}
														</span>
													</div>
												</div>
											);
										})}
									</div>
								)}
								cursor={false}
							/>
							<Pie
								animationDuration={1000}
								animationEasing="ease"
								data={chartData}
								dataKey="value"
								innerRadius="68%"
								nameKey="name"
								paddingAngle={-20}
								strokeWidth={0}
							>
								{chartData.map((_, index) => (
									<Cell
										key={`cell-${index}`}
										fill={`hsl(var(--heroui-${color}-${(index + 1) * 200}))`}
									/>
								))}
							</Pie>
						</PieChart>
					</ResponsiveContainer>

					<div className="text-tiny text-default-500 flex w-full flex-col justify-center gap-4 p-4 lg:p-0">
						{categories.map((category, index) => (
							<div
								key={index}
								className="flex items-center gap-2"
							>
								<span
									className="h-2 w-2 rounded-full"
									style={{
										backgroundColor: `hsl(var(--heroui-${color}-${(index + 1) * 200}))`,
									}}
								/>
								<span className="capitalize">{category}</span>
							</div>
						))}
					</div>
				</div>
			</Card>
		);
	},
);

CircleChartCard.displayName = "CircleChartCard";
